"use client";

import { useAuthenticationStore } from "@/core/stores/auth";
import useSetSearchParams from '@/hooks/useSetSearchParams';
import { useEffect, useState } from "react";
import AccordeonBasic from '../AccorderonBasic';
import { sendEmail } from '@/actions/authentication';
import Button from '../ui/Button';

const LoginForm = ({ apiUrl, setShowCodeInput, onSubmit, hideReferralInput = false, isLoading }: {
  apiUrl?: string, setShowCodeInput: any, onSubmit?: () => void, hideReferralInput?: boolean,
  isLoading?: boolean
}) => {
  const { email, setEmail } = useAuthenticationStore()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { params: { referralCode } } = useSetSearchParams()

  useEffect(() => {
    if (referralCode) {
      setCode(referralCode)
    }
  }, [referralCode])

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmitForm = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setError('Email is wrong, please change it.')
      return
    } else {
      setError('')
    }

    setLoading(true)
    const body: any = {
      email: email
    }

    if (code) {
      body.referralCode = code
    }
    const res = await sendEmail(body)

    const { result, response } = JSON.parse(res)

    if (response.status === 200) {
      setShowCodeInput(true)
      if (onSubmit) {
        onSubmit()
      }
    } else {

      setError(result.message)
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmitForm} className="flex flex-col gap-2">
      <div>
        <label
          htmlFor="email"
          className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
        >
          Enter your Email
        </label>
        <input
          type="text"
          id="email"
          name="email"
          value={email}
          onChange={((e) => setEmail(e.target.value))}
          className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-gray-600 focus:ring-0 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
          placeholder="name@example.com"
        />
      </div>
      {!hideReferralInput && <div className='mt-2'>
        <AccordeonBasic
          reverseCheveron
          title={<label
            htmlFor="referral"
            className="mb-2 block text-sm font-medium text-indigo-500 dark:text-white"
          >
            If you have referral code enter here
          </label>}>
          <input
            type="text"
            id="referral"
            name="referral"
            value={code}
            onChange={((e) => setCode(e.target.value))}
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-gray-600 focus:ring-0 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
            placeholder="Your referral code"
          />
        </AccordeonBasic>

      </div>}
      <p className=" text-sm font-normal text-gray-500 pb-2">
        We will send you an Email with a verification code.
      </p>
      <Button
        radius='rounded-full'
        color='secondary'
        type="submit"
        disabled={loading || isLoading}
        className={`${loading || isLoading ? 'bg-gray-550/70' : ''}
                 w-full   text-sm text-white`}
      >
        {(loading || isLoading) ? <div role="status">
          <svg aria-hidden="true" className="inline w-5 h-5 text-gray-200 animate-spin  fill-gray-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
          : <span>Send verification code</span>
        }
      </Button>
      {error && <p className="text-rose-500 text-sm">{error}</p>}
    </form>
  );
};

export default LoginForm;
